import React, { useCallback, useEffect, useState, useRef, useId, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
// Reusable address autocomplete input.
// - Queries Photon (https://photon.komoot.io/) — free, no API key, fast,
//   autocomplete-optimized OSM search. Biased to the South Sound delivery area.
// - Renders a styled suggestion dropdown with keyboard navigation.
// - When the user picks a suggestion, fires `onSelect(address, lat, lon)`
//   so the parent can skip re-geocoding and act on the coordinates directly.
// - Falls back to the parent's existing debounced geocode if the user types
//   without picking a suggestion.
const PHOTON_URL = 'https://photon.komoot.io/api/';
// Center bias — Tacoma area. Same as DELIVERY_CENTER.
const BIAS_LAT = 47.2073;
const BIAS_LON = -122.197;
export type AddressSuggestion = {
  label: string;
  fullAddress: string;
  lat: number;
  lon: number;
};
type Props = {
  value: string;
  onChange: (val: string) => void;
  onSelect?: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  inputClassName?: string;
  containerClassName?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
  required?: boolean;
  autoComplete?: string;
  inputId?: string;
  inputAriaLabel?: string;
  /** Style for the suggestion dropdown ("dark" matches the Hero glass card,
   * "light" matches light surfaces). */
  variant?: 'dark' | 'light';
  /** Open the dropdown above the input instead of below. Useful for
   * inputs anchored to the bottom of the viewport. */
  dropDirection?: 'down' | 'up';
};
type PhotonFeature = {
  geometry: {
    coordinates: [number, number];
  };
  properties: {
    osm_id?: number;
    name?: string;
    housenumber?: string;
    street?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
    countrycode?: string;
    type?: string;
    osm_value?: string;
  };
};
function formatSuggestion(f: PhotonFeature): AddressSuggestion | null {
  const p = f.properties;
  if (p.countrycode && p.countrycode !== 'US') return null;
  if (p.state && p.state !== 'Washington') return null;
  const street = [p.housenumber, p.street].filter(Boolean).join(' ').trim();
  const cityBit = [p.city, p.state ? 'WA' : undefined, p.postcode].filter(Boolean).join(', ');
  // Need at least a street OR a recognizable name to be useful.
  const primary = street || p.name || '';
  if (!primary) return null;
  const full = [primary, cityBit].filter(Boolean).join(', ');
  return {
    label: primary,
    fullAddress: full,
    lat: f.geometry.coordinates[1],
    lon: f.geometry.coordinates[0]
  };
}
export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  inputClassName = '',
  containerClassName = '',
  leftIcon,
  rightIcon,
  isLoading = false,
  required,
  autoComplete = 'street-address',
  inputId,
  inputAriaLabel,
  variant = 'dark',
  dropDirection = 'down'
}: Props) {
  const reactId = useId();
  const listboxId = `${inputId || reactId}-listbox`;
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const [isFetching, setIsFetching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Track the value the parent just received from a user pick — used to
  // suppress the next debounced fetch (which would just re-search for the
  // selected address).
  const justSelectedRef = useRef(false);
  // Close on click outside.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  // Debounced Photon query as the user types.
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    const q = value.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      setActiveIdx(-1);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      setIsFetching(true);
      try {
        const url = `${PHOTON_URL}?q=${encodeURIComponent(q)}&lat=${BIAS_LAT}&lon=${BIAS_LON}&limit=6&lang=en`;
        const res = await fetch(url, {
          signal: ctrl.signal
        });
        const data = await res.json();
        const feats: PhotonFeature[] = data?.features || [];
        const mapped: AddressSuggestion[] = [];
        const seen = new Set<string>();
        for (const f of feats) {
          const s = formatSuggestion(f);
          if (!s) continue;
          if (seen.has(s.fullAddress)) continue;
          seen.add(s.fullAddress);
          mapped.push(s);
          if (mapped.length >= 5) break;
        }
        setSuggestions(mapped);
        setOpen(mapped.length > 0);
        setActiveIdx(-1);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setSuggestions([]);
          setOpen(false);
        }
      } finally {
        setIsFetching(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [value]);
  const handleSelect = useCallback((s: AddressSuggestion) => {
    justSelectedRef.current = true;
    onChange(s.fullAddress);
    setSuggestions([]);
    setOpen(false);
    setActiveIdx(-1);
    onSelect?.(s);
  }, [onChange, onSelect]);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => i <= 0 ? suggestions.length - 1 : i - 1);
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && activeIdx < suggestions.length) {
        e.preventDefault();
        handleSelect(suggestions[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    }
  };
  const dropdownStyles = variant === 'light' ? 'bg-white border-stone-200 text-stone-900 shadow-2xl' : 'bg-stone-900/95 border-white/10 text-white shadow-2xl backdrop-blur-md';
  const itemHover = variant === 'light' ? 'hover:bg-stone-100' : 'hover:bg-white/10';
  const itemActive = variant === 'light' ? 'bg-stone-100' : 'bg-white/10';
  const subText = variant === 'light' ? 'text-stone-500' : 'text-white/55';
  return <div className={`relative ${containerClassName}`} ref={containerRef} data-id="element-143">
      {leftIcon && <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10" data-id="element-144">
          {leftIcon}
        </div>}
      <input id={inputId} type="text" value={value} onChange={e => onChange(e.target.value)} onFocus={() => suggestions.length > 0 && setOpen(true)} onKeyDown={handleKeyDown} placeholder={placeholder} autoComplete={autoComplete} required={required} aria-label={inputAriaLabel} aria-autocomplete="list" aria-expanded={open} aria-controls={listboxId} aria-activedescendant={activeIdx >= 0 ? `${listboxId}-opt-${activeIdx}` : undefined} role="combobox" className={inputClassName} data-id="element-145" />
      {(isLoading || isFetching) && <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none z-10" data-id="element-146">
          <Loader2 className="w-4 h-4 text-[#1DB954] animate-spin" data-id="element-147" />
        </div>}
      {!isLoading && !isFetching && rightIcon && <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none z-10" data-id="element-148">
          {rightIcon}
        </div>}
      <AnimatePresence data-id="element-149">
        {open && suggestions.length > 0 && <motion.ul id={listboxId} role="listbox" initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15
      }} className={`absolute left-0 right-0 z-50 rounded-lg border overflow-hidden ${dropDirection === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'} ${dropdownStyles}`} data-id="element-150">
            {suggestions.map((s, i) => {
          const active = i === activeIdx;
          return <li id={`${listboxId}-opt-${i}`} key={`${s.lat},${s.lon},${i}`} role="option" aria-selected={active} onMouseDown={e => {
            // mousedown (not click) so we beat the blur that would
            // otherwise close the dropdown before the click fires.
            e.preventDefault();
            handleSelect(s);
          }} onMouseEnter={() => setActiveIdx(i)} className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${active ? itemActive : itemHover}`} data-id="element-151">
                  <div className="font-medium leading-tight" data-id="element-152">{s.label}</div>
                  <div className={`text-xs mt-0.5 leading-tight ${subText}`} data-id="element-153">
                    {s.fullAddress.replace(`${s.label}, `, '') || s.fullAddress}
                  </div>
                </li>;
        })}
          </motion.ul>}
      </AnimatePresence>
    </div>;
}